import React, { useCallback, useMemo, useState } from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Alert, Box, CircularProgress, IconButton, Link, Stack, Typography } from '@mui/material';

import { ROOM_RATE_LPC_TYPE } from '~/consts/ratePlans';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { RATE_PLAN_ACTIVE_STATUS, RatePlan } from '~/hooks/apis/reservation/usePropertyRatePlans';

import { downloadCsvFile } from '~/utils/csvDownload';
import currencyFormatter from '~/utils/currencyFormatter';

import { CARD_SX, HIGHLIGHTED_CARD_SX } from './constants';
import { safeParityFilenameSlug } from './safeParityFilenameSlug';
import { selectCheapestLPCRatePlan } from './selectCheapestLPCRatePlan';

interface RatePlanInfoProps {
  ratePlans: Array<RatePlan>;
  isLoading: boolean;
  error?: unknown;
  currency: string;
  propertyName: string;
  analysisDate: string;
  lpcPromotionalDiscounts?: Array<App.RatePlanPromotion>;
  promotionsLoading?: boolean;
  promotionsError?: unknown;
}

const INITIAL_VISIBLE_COUNT = 2;
const INITIAL_VISIBLE_PROMOS = 3;

const cardStyles = {
  highlighted: HIGHLIGHTED_CARD_SX,
  default: { ...CARD_SX, p: 3 },
};

const labelStyles = {
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: 0.5,
  mb: 1,
};

const sectionHeadingStyles = {
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: 0.5,
};

const ACTIVE_RATE_PLAN_CSV_HEADERS = ['name', 'rate_plan_code', 'price', 'currency'] as const;
const PROMOTIONS_CSV_HEADERS = ['discount_name', 'discount_percent'] as const;

function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  ratesMap: Record<string, number>,
): number | null {
  const fromRate = ratesMap[fromCurrency];
  const toRate = ratesMap[toCurrency];
  if (fromRate == null || toRate == null) {
    return null;
  }
  return (amount * toRate) / fromRate;
}

function activeRatePlanRowsForCsv(plans: Array<RatePlan>): Array<Record<string, unknown>> {
  return plans.map((plan) => ({
    name: plan.name ?? '',
    rate_plan_code: plan.rate_plan_code ?? '',
    price: plan.price != null ? plan.price : '',
    currency: plan.costCurrency ?? '',
  }));
}

function promotionRowsForCsv(promos: Array<App.RatePlanPromotion>): Array<Record<string, unknown>> {
  return promos.map((promo) => ({
    discount_name: promo.discount_name ?? '',
    discount_percent: promo.discount_percent ?? '',
  }));
}

export default function RatePlanInfo({
  ratePlans,
  isLoading,
  error,
  currency,
  propertyName,
  analysisDate,
  lpcPromotionalDiscounts = [],
  promotionsLoading = false,
  promotionsError,
}: RatePlanInfoProps) {
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [showAllPromos, setShowAllPromos] = useState(false);

  const needsFxConversion = ratePlans.some(
    (p) => p.product_type === ROOM_RATE_LPC_TYPE && p.costCurrency && p.costCurrency !== currency && p.price != null,
  );
  const { data: fxRates = [] } = useGetFxRates(needsFxConversion);

  const fxRatesMap = useMemo(() => {
    const map: Record<string, number> = { AUD: 1 };
    for (const rate of fxRates) {
      map[rate.currency] = rate.rate;
    }
    return map;
  }, [fxRates]);

  const slug = useMemo(() => safeParityFilenameSlug(propertyName), [propertyName]);

  const activeLpcPlans = useMemo(
    () =>
      ratePlans.filter(
        (plan) => plan.product_type === ROOM_RATE_LPC_TYPE && plan.status?.toLowerCase() === RATE_PLAN_ACTIVE_STATUS,
      ),
    [ratePlans],
  );

  const handleDownloadActiveLpcRatePlans = useCallback(() => {
    downloadCsvFile(
      activeRatePlanRowsForCsv(activeLpcPlans),
      [...ACTIVE_RATE_PLAN_CSV_HEADERS],
      `active_rate_plans_${slug}_${analysisDate}.csv`,
    );
  }, [activeLpcPlans, slug, analysisDate]);

  const handleDownloadPromotions = useCallback(() => {
    downloadCsvFile(
      promotionRowsForCsv(lpcPromotionalDiscounts),
      [...PROMOTIONS_CSV_HEADERS],
      `active_promotions_${slug}_${analysisDate}.csv`,
    );
  }, [lpcPromotionalDiscounts, slug, analysisDate]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Unable to load rate plans
      </Alert>
    );
  }

  if (ratePlans.length === 0) {
    return (
      <Box sx={cardStyles.default}>
        <Typography variant="body2" color="text.secondary">
          No rate plan data available
        </Typography>
      </Box>
    );
  }

  const cheapestPlan = selectCheapestLPCRatePlan(ratePlans);
  if (!cheapestPlan) {
    return (
      <Box sx={cardStyles.default}>
        <Typography variant="body2" color="text.secondary">
          No rate plans to display
        </Typography>
      </Box>
    );
  }

  const priceCurrency = cheapestPlan.costCurrency || currency;
  const showConversion = cheapestPlan.price != null && priceCurrency !== currency;
  const convertedPrice = showConversion
    ? convertCurrency(cheapestPlan.price, priceCurrency, currency, fxRatesMap)
    : null;
  const visiblePlans = showAllPlans ? activeLpcPlans : activeLpcPlans.slice(0, INITIAL_VISIBLE_COUNT);
  const visiblePromos = showAllPromos
    ? lpcPromotionalDiscounts
    : lpcPromotionalDiscounts.slice(0, INITIAL_VISIBLE_PROMOS);

  const showPromoDownload = lpcPromotionalDiscounts.length > 0 && !promotionsError;

  return (
    <Stack spacing={2}>
      <Typography sx={{ fontSize: 22, fontWeight: 700 }}>Your Rate Strategy</Typography>
      <Box sx={cardStyles.highlighted}>
        <Typography sx={labelStyles}>CHEAPEST SINGLE-NIGHT 2-ADULT RATE</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{cheapestPlan.name ?? '—'}</Typography>
        {cheapestPlan.rate_plan_code && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>(Code: {cheapestPlan.rate_plan_code})</Typography>
        )}
        <Typography sx={{ fontSize: 32, fontWeight: 700, color: 'success.main', mt: 1 }}>
          {cheapestPlan.price != null ? currencyFormatter(priceCurrency, cheapestPlan.price, 0) : '—'}
        </Typography>
        {convertedPrice != null && (
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            ≈ {currencyFormatter(currency, convertedPrice, 0)} {currency}
          </Typography>
        )}
      </Box>

      <Box sx={cardStyles.default}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography sx={sectionHeadingStyles}>ACTIVE RATE PLANS ({activeLpcPlans.length})</Typography>
          {activeLpcPlans.length > 0 ? (
            <IconButton
              size="small"
              aria-label="Download active rate plans as CSV"
              title="Download as CSV"
              onClick={handleDownloadActiveLpcRatePlans}
              sx={{ mt: -0.5, mr: -0.5 }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Stack>
        <Stack spacing={0.5}>
          {visiblePlans.map((plan) => (
            <Typography key={plan.id} variant="body2">
              {plan.name ?? plan.rate_plan_code ?? plan.id}
            </Typography>
          ))}
        </Stack>
        {activeLpcPlans.length > INITIAL_VISIBLE_COUNT && (
          <Link
            component="button"
            onClick={() => setShowAllPlans((prev) => !prev)}
            sx={{ mt: 1, fontSize: 13, color: 'primary.main', textDecoration: 'none', cursor: 'pointer' }}
          >
            {showAllPlans ? 'Show less' : 'Show all'}
          </Link>
        )}
      </Box>

      <Box sx={cardStyles.default}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography sx={sectionHeadingStyles}>
            CURRENT PROMOTIONS ({lpcPromotionalDiscounts.length}){promotionsLoading ? ' · loading…' : ''}
          </Typography>
          {showPromoDownload ? (
            <IconButton
              size="small"
              aria-label="Download active promotions as CSV"
              title="Download as CSV"
              onClick={handleDownloadPromotions}
              sx={{ mt: -0.5, mr: -0.5 }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Stack>
        {promotionsError ? (
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            Unable to load promotions
          </Alert>
        ) : promotionsLoading && lpcPromotionalDiscounts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : lpcPromotionalDiscounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No promotion data available
          </Typography>
        ) : (
          <>
            <Stack spacing={0.5}>
              {visiblePromos.map((promo) => {
                const label = promo.discount_name || promo.discount_type || 'Promotion';
                const pct =
                  promo.discount_type === 'percentage' || typeof promo.discount_percent === 'number'
                    ? `${promo.discount_percent}%`
                    : String(promo.discount_percent ?? '');
                return (
                  <Typography key={promo.id} variant="body2">
                    {label}
                    {pct ? ` · ${pct}` : ''}
                  </Typography>
                );
              })}
            </Stack>
            {lpcPromotionalDiscounts.length > INITIAL_VISIBLE_PROMOS && (
              <Link
                component="button"
                onClick={() => setShowAllPromos((prev) => !prev)}
                sx={{ mt: 1, fontSize: 13, color: 'primary.main', textDecoration: 'none', cursor: 'pointer' }}
              >
                {showAllPromos ? 'Show less' : 'Show all'}
              </Link>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
